import QuickViewProductSummary from "components/QuickViewWorkOrder/QuickViewProductSummary";
import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import ImageWIthSignedUrl from "components/S3Utility/ImageWIthSignedUrl";
import { useCallback, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import ReactQuill from "react-quill";
import { useDispatch } from "react-redux";
import { Link, useSearchParams } from "react-router-dom";
import authHeader from "services/auth-header";
import API from "services/axios";
import { GetNameWithTimeStamp, parsedJsonString } from "utils/UtilityFuncs";
import SProductQty from "./SProductQty";
import SProductsInfo from "./SProductsInfo";

// A size set adds all its sizes; Add / Save keeps only the ones given a
// quantity (user, 2026-10-29). Enter on a size cell saves without this, so the
// empty rows stay while the user is still filling them in.
const dropEmptySizes = (form) => {
  const sizes = parsedJsonString(form?.productSizes);
  if (!Array.isArray(sizes)) return form;
  return {
    ...form,
    productSizes: JSON.stringify(sizes.filter((s) => parseInt(s?.quantity) > 0)),
  };
};

export default function SOAddUpdateProduct({
  showModal,
  allProducts,
  setAllProducts,
  selectedWO,
  refreshFunc,
  setUpdateId,
  lastSortId,
  selectMode = false,
  onSelectProduct,
  removeUpdateId,
  approvalStatus,
  approvalData,
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const isUpdateMode = !!searchParams.get("productId");
  const dispatch = useDispatch();
  /*   All States
   ********************************************* */

  const modules = {
    toolbar: [
      [{ font: [] }],
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }],
      [{ indent: "-1" }, { indent: "+1" }, { align: [] }],
      ["clean"],
    ],
  };

  const [formData, setFormData] = useState({});
  const [previewImage, setPreviewImage] = useState(null);

  const [fabrics, setFabrics] = useState([]);

  const [productsList, setProductsList] = useState([]);
  const [productTypesList, setProductTypesList] = useState([]);
  const [selectedProductType, setSelectedProductType] = useState(null);
  const [isAllReady, setIsAllReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [productTypeSummary, setProductTypeSummary] = useState("");
  const [productSummaryModal, setProductSummaryModal] = useState(false);
  const [productAggregatedSizes, setProductAggregatedSizes] = useState({});

  /*   All Functions
   ********************************************* */
  const loadingOn = () => {
    setLoading(true);
  };

  const loadingOff = () => {
    setLoading(false);
  };

  /*   ADD Func
   **************** */
  const AddFunc = async (e) => {
    e.preventDefault();

    if (!formData?.productTypeId || formData?.productTypeId === "") {
      alert("Please select product type");
      return;
    }
    loadingOn();
    try {
      let _formData = new FormData();
      let bodyData = {
        ...dropEmptySizes(formData),
        sortId: lastSortId + 1,
        WOId: selectedWO?.id,
      };

      //Appending File if present.
      if (formData?.file) {
        let file = formData?.file;
        _formData.append("file", file);
        let folder = `kms/wo/${selectedWO?.id}/products`;
        _formData.append("folder", folder);
        // Creating filePath of upload folder to save it to database
        let fileName = GetNameWithTimeStamp(file.name);
        let productImage = `${folder}/${fileName}`;
        bodyData.productImage = productImage;
        _formData.append("fileName", fileName);
      }

      // Assigning update data to formData in JSON string
      _formData.append("data", JSON.stringify(bodyData));

      const { data } = await API.post(`/wo-products`, _formData, {
        headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
      });

      if (data) {
        await refreshFunc();
        showModal(false);
        setPreviewImage(null);
        loadingOff();
        return;
      }
      loadingOff();
    } catch (err) {
      alert(err.message);
      loadingOff();
    }
  };
  /*   Update Func
   **************** */
  const UpdateFunc = async (e, updatedSizeForm, saveButton = false) => {
    if (e) {
      e.preventDefault();
    }
    setLoading(true);

    if (!formData?.productTypeId || formData?.productTypeId === "") {
      alert("Please select product type");
      return;
    }

    let newForm = updatedSizeForm || formData;
    if (saveButton) newForm = dropEmptySizes(newForm);

    // Persist current selection so it stays selected after refresh
    const targetId = newForm?.id || formData?.id;
    if (targetId) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("productId", String(targetId));
        return next;
      });
    }

    try {
      let _formData = new FormData();
      let bodyData = { ...newForm, id: newForm?.id };

      //Appending File if present.
      if (formData?.file) {
        let file = formData?.file;
        _formData.append("file", file);
        let folder = `kms/wo/${selectedWO?.id}/products`;
        _formData.append("folder", folder);
        // Creating filePath of upload folder to save it to database
        let fileName = GetNameWithTimeStamp(file.name);
        let productImage = `${folder}/${fileName}`;
        bodyData.productImage = productImage;
        _formData.append("fileName", fileName);
      }

      // Assigning update data to formData in JSON string
      _formData.append("data", JSON.stringify(bodyData));

      const { data } = await API.patch(`/wo-products`, _formData, {
        headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
      });
      if (data.message.type === "success") {
        await refreshFunc();
        setLoading(false);
        setPreviewImage(null);
        return;
      }
    } catch (err) {
      setLoading(true);
      if (err?.response?.status === 413) {
        alert("Size Too Large");
        setLoading(false);
        return;
      }
      alert(err.message);
    }
  };

  /*   Save Notes Only
   **************** */
  // Sends just { id, notes } rather than the whole row. WOProducts.update()
  // only writes the keys it's given, so nothing else on the product is touched
  // — this avoids overwriting fields with stale form state. WOId is left out
  // deliberately: that's what triggers the approval recalc on the backend, and
  // a notes edit can't change approval status, so there's no reason to log an
  // approval change against whoever saved a note.
  const SaveNotesFunc = async () => {
    if (!formData?.id) return;

    setLoading(true);
    try {
      let _formData = new FormData();
      _formData.append(
        "data",
        JSON.stringify({ id: formData.id, notes: formData?.notes ?? "" }),
      );

      const { data } = await API.patch(`/wo-products`, _formData, {
        headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
      });

      if (data?.message?.type === "success") {
        await refreshFunc();
        dispatch(
          setNotification({
            message: "Notes saved",
            type: "success",
          }),
        );
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    }
  };

  const getReadyStatusForCurrentProductType = () => {
    if (!selectedProductType) {
      return false;
    }

    // set ready to true only if all products of selected type are ready
    setIsAllReady(
      productsList?.every(
        (product) =>
          product?.productTypeId === selectedProductType?.productTypeId &&
          product.isReady,
      ),
    );
  };

  const UpdateProductReady = async ({ ready }) => {
    try {
      loadingOn(true);

      const { data } = await API.patch(
        "/wo-products/ready-status",
        {
          WOId: selectedWO?.id,
          productTypeId: selectedProductType?.productTypeId,
          isReady: ready,
        },
        { headers: authHeader() },
      );

      if (data?.success) {
        // update all products where productType matches
        setProductsList((prev) => {
          let updatedProducts = [...prev];
          updatedProducts.forEach((product) => {
            if (product?.productTypeId === selectedProductType?.productTypeId) {
              product.isReady = ready;
            }
          });
          return updatedProducts;
        });

        dispatch(
          setNotification({
            message: data.message,
            type: "success",
          }),
        );
      }

      loadingOff();
    } catch (err) {
      loadingOff();
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    }
  };

  const getBase64Image = async (e) => {
    const file = e.target.files[0];

    // const MAX_FILE_SIZE = 300 * 1024; // 800 KB in bytes
    // if (!["image/jpeg", "image/png"].includes(file.type)) {
    //   alert("Only JPG and PNG image files are allowed.");
    //   e.target.value = "";
    //   return;
    // }
    // if (file.size > MAX_FILE_SIZE) {
    //   alert(`File size exceeds limit (300 KB). Please select a smaller file.`);
    //   e.target.value = "";
    //   return;
    // }

    setFormData((prev) => {
      return { ...prev, file };
    });
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = () => {
      setPreviewImage(reader?.result);
    };
  };

  const getAllProductTypes = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await API.get("/productTypes", {
        headers: authHeader(),
      });
      setProductTypesList(data);
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  const handleProductTypeFilter = (item, tabProductId = null) => {
    setSelectedProductType(item);
    if (item === null) {
      setProductsList(allProducts);
      setFormData(allProducts[0]);
      return;
    }

    let filteredList = allProducts.filter((product) => {
      return product?.productTypeId === item?.productTypeId;
    });

    if (filteredList.length === 0) {
      setProductsList(allProducts);
      setFormData(allProducts[0]);
      setSelectedProductType(null);
      return;
    }

    setProductsList(filteredList);
    let productId = tabProductId || searchParams.get("productId");

    let _selectedProduct = filteredList.find(
      (product) => product.id === parseInt(productId),
    );

    setFormData(_selectedProduct || filteredList[0]);
  };

  const handleTabClick = (item) => {
    // get wo products filtered by item.productType
    let filteredProducts = allProducts?.filter(
      (product) => product?.productTypeId === item?.productTypeId,
    );

    if (filteredProducts.length === 0) {
      setProductsList(allProducts);
      setFormData(allProducts[0]);
      setSelectedProductType(null);
      return;
    }

    setUpdateId(filteredProducts[0]?.id);
    handleProductTypeFilter(item, filteredProducts[0]?.id);
  };

  /**
   * @param(selectedType) - filterBy
   * Counts the number products of selected type and total quantity of that type.
   * @returns(Number,total quantity) - Number of products of selected type.
   */
  const countProducts = (selectedType) => {
    if (!selectedType) {
      const summary = allProducts?.reduce(
        (acc, item) => {
          acc.totalQuantity += item.totalQuantity;
          acc.count += 1;
          return acc;
        },
        { totalQuantity: 0, count: 0 },
      );

      return summary;
    }

    const summary = allProducts?.reduce(
      (acc, item) => {
        if (item.productTypeId === selectedType.productTypeId) {
          acc.totalQuantity += item.totalQuantity;
          acc.count += 1;
        }
        return acc;
      },
      { totalQuantity: 0, count: 0 },
    );

    return summary;
  };

  // get total quantity of all products
  const getTotalQuantity = () => {
    return allProducts?.reduce((acc, item) => {
      acc += item.totalQuantity;
      return acc;
    }, 0);
  };

  // get All Fabrics
  const getFabrics = useCallback(async () => {
    try {
      setLoading(true);

      let page = 1;
      let limit = 999;
      let query = "";
      let filterBy = "all";

      let body = { page, limit, filterBy };

      if (query) {
        body.query = query;
      }

      const { data } = await API.post("sales/products/fabrics", body, {
        headers: authHeader(),
      });

      if (data.success) {
        setFabrics(data.data);
        return data.data;
      } else {
        dispatch(
          setNotification({
            message: data.message,
            type: "error",
          }),
        );
      }
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  const handleProductSummary = (item, productsToUse) => {
    const list =
      (productsToUse || allProducts)?.filter(
        (product) => product?.productTypeId === item?.productTypeId,
      ) || [];

    setProductTypeSummary(list);
    setProductSummaryModal(true);
  };

  const handleAllProductSummary = () => {
    setProductTypeSummary(allProducts || []);
    setProductSummaryModal(true);
  };

  // const handleProductSummary = (selectedType) => {
  //   const productsToSummarize = productsList || [];

  //   // Build simple product listing for left side
  //   const formattedProductDetail = {};
  //   productsToSummarize.forEach((product, i) => {
  //     formattedProductDetail[`Product ${i + 1}`] =
  //       `${product.productStyle}  -  ${product.totalQuantity}`;
  //   });

  //   // Aggregate sizes by productStyle for right side
  //   const agg = {};
  //   productsToSummarize.forEach((product) => {
  //     const style = product.productStyle || "Unknown";
  //     agg[style] = agg[style] || {};

  //     let sizes = [];
  //     if (product.productSizes) {
  //       try {
  //         sizes =
  //           typeof product.productSizes === "string"
  //             ? JSON.parse(product.productSizes)
  //             : product.productSizes;
  //       } catch (err) {
  //         sizes = [];
  //       }
  //     }

  //     sizes.forEach((s) => {
  //       const sizeKey = (s?.size || "").toString().toUpperCase() || "UNDEFINED";
  //       const qty = parseInt(s?.quantity) || 0;
  //       agg[style][sizeKey] = (agg[style][sizeKey] || 0) + qty;
  //     });
  //   });

  //   setProductTypeSummary(formattedProductDetail);
  //   setProductAggregatedSizes(agg);
  //   setProductSummaryModal(true);
  // };

  // Get Index of selected Product
  const getSelectedProductIndex = () => {
    if (productsList?.length === 0) {
      return;
    }
    let currentIndex = productsList.findIndex(
      (item) => item.id === formData.id,
    );
    return currentIndex;
  };

  //  Next Product
  const nextProduct = () => {
    if (productsList?.length === 0) {
      return;
    }
    let currentIndex = getSelectedProductIndex();

    // If the current index is the last index
    if (currentIndex === productsList.length - 1) {
      setFormData(productsList[0]);
      // setUpdateId(productsList[0].id);
      return;
    }
    // setUpdateId(productsList[currentIndex + 1].id);
    setFormData(productsList[currentIndex + 1]);
  };

  //  Previous Product
  const prevProduct = () => {
    if (productsList?.length === 0) {
      return;
    }
    let currentIndex = getSelectedProductIndex();

    // If the current index is the first index
    if (currentIndex === 0) {
      setFormData(productsList[productsList.length - 1]);
      // setUpdateId(productsList[productsList.length - 1].id);
      return;
    }

    // setUpdateId(productsList[currentIndex - 1].id);
    setFormData(productsList[currentIndex - 1]);
  };

  /*   All UseEffects
   ********************************************* */
  useEffect(() => {
    // if (!isUpdateMode) return;
    const productId = searchParams.get("productId");
    if (productId === "none") {
      setProductsList(allProducts || []);
      setFormData(allProducts[0]);
      handleProductTypeFilter(null);
    }

    // Prefer the current WO's products; they refresh with the latest data
    const products = allProducts || [];
    const pid = parseInt(productId);

    const product = products.find((p) => p.id === pid);

    if (!product) return;

    // Build filtered list matching the product's type, keep selection
    const filteredList = products.filter(
      (p) => p.productTypeId === product.productTypeId,
    );

    setProductsList(filteredList.length ? filteredList : products);

    // Try to select matching type object for tabs (if available)
    const typeObj = productTypesList.find(
      (t) => t?.productTypeId === product?.productTypeId,
    );
    handleProductTypeFilter(typeObj || null);

    // Finally set the active product

    setFormData(product);
  }, [isUpdateMode, searchParams, allProducts, productTypesList]);

  useEffect(() => {
    let timeout = setTimeout(() => {
      getAllProductTypes();
      getFabrics();
    }, 50);
    return () => clearTimeout(timeout);
  }, [getAllProductTypes, getFabrics]);

  // In selection mode, preload the list and a default selection
  useEffect(() => {
    if (!selectMode) return;
    const products = allProducts || [];
    if (!products.length) return;
    setProductsList(products);
    const pidParam = searchParams.get("productId");
    const pid =
      pidParam && !Number.isNaN(parseInt(pidParam)) ? parseInt(pidParam) : null;
    const initial = pid ? products.find((p) => p.id === pid) : products[0];
    setFormData(initial || products[0]);
  }, [selectMode, allProducts, searchParams]);

  useEffect(() => {
    if (selectedProductType) {
      getReadyStatusForCurrentProductType();
    }
  }, [selectedProductType, formData?.isReady]);

  const headerTitle = selectMode
    ? "Select Product"
    : isUpdateMode
      ? "Update Product"
      : "Add Product";

  return (
    <fieldset disabled={loading}>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center px-1 pb-1 mb-2 border-bottom border-2">
        <div className="fs-4">
          <h3>{headerTitle}</h3>
        </div>
        <div className="d-flex justify-content-end align-items-center ">
          {formData && isUpdateMode && (
            <button
              onClick={SaveNotesFunc}
              className="btn btn-outline-success btn-sm me-2 text-nowrap"
            >
              Save Notes
            </button>
          )}
          {formData && isUpdateMode && (
            <>
              <div className="d-flex align-items-center gap-2 me-2">
                <label className="col-5">Ready?</label>
                <div className="col-7 ">
                  <input
                    onChange={(e) => {
                      e.stopPropagation();
                      setIsAllReady(e.target.checked);
                      UpdateProductReady({
                        id: formData?.id,
                        ready: e.target.checked,
                      });
                    }}
                    disabled={!selectedProductType}
                    checked={isAllReady}
                    type="checkbox"
                    id="switch1"
                    data-switch="success"
                  />
                  <label
                    htmlFor="switch1"
                    data-on-label="Yes"
                    data-off-label="No"
                  ></label>
                </div>
              </div>
            </>
          )}
          {selectMode ? (
            <button
              onClick={() => onSelectProduct && onSelectProduct(formData)}
              className={"btn me-1 btn-success"}
            >
              Use This Product
            </button>
          ) : (
            <button
              onClick={
                isUpdateMode ? (e) => UpdateFunc(e, null, true) : AddFunc
              }
              className={
                "btn me-1 " + (isUpdateMode ? "btn-success" : "btn-primary")
              }
            >
              {isUpdateMode ? "Save" : "Add"}
            </button>
          )}

          {/*   WO Product Buttons
           ********************************************* */}
          <button
            onClick={() => {
              prevProduct();
            }}
            disabled={productsList?.length === 0}
            className={"btn btn-success btn-sm p-0 px-1 me-1 "}
          >
            <i className="mdi mdi-arrow-left-bold fs-3 py-0 my-0"></i>
          </button>
          <button
            onClick={() => {
              nextProduct();
            }}
            disabled={productsList?.length === 0}
            className={"btn btn-success btn-sm p-0 px-1"}
          >
            <i className="mdi mdi-arrow-right-bold fs-3 py-0 my-0"></i>
          </button>
          <button
            onClick={() => {
              showModal(false);
            }}
            className={"btn btn-danger ms-1"}
          >
            BACK
          </button>
        </div>
      </div>
      {/* Product Filters */}
      {/* List of Filters buttons for product by productType */}
      {(isUpdateMode || selectMode) && (
        <div className="my-1 d-flex align-items-center justify-content-between">
          <div className="d-flex gap-1">
            <button
              onClick={() => {
                handleProductTypeFilter(null);
              }}
              onDoubleClick={() => {
                handleAllProductSummary(null);
              }}
              className={`btn btn-sm ${
                !selectedProductType ? "btn-primary" : "btn-outline-primary"
              }`}
            >
              All ({allProducts?.length}) - {getTotalQuantity()}
            </button>
            {productTypesList &&
              productTypesList?.map((item) => {
                if (countProducts(item) && countProducts(item)["count"] === 0)
                  return null;
                return (
                  <button
                    key={item.id}
                    onClick={() => {
                      handleTabClick(item);
                    }}
                    onDoubleClick={() => {
                      handleProductSummary(item);
                    }}
                    className={`btn btn-sm ${
                      selectedProductType?.id === item.id
                        ? "btn-primary"
                        : "btn-outline-primary"
                    }`}
                  >
                    {item?.productTypes} (
                    {countProducts(item) &&
                      `${countProducts(item)["count"]} - ${
                        countProducts(item)["totalQuantity"]
                      }`}
                    )
                  </button>
                );
              })}
          </div>
          <div className="d-flex align-items-center gap-2">
            {approvalData !== "loading" && approvalStatus && (
              <div className="d-flex align-items-center gap-2">
                <span
                  className={`btn btn-sm  ${approvalStatus.badgeClass}`}
                  style={approvalStatus.badgeStyle}
                >
                  {approvalStatus.badgeText}
                </span>
                {approvalData?.approvalLink && (
                  <Link
                    to={`/work-order/approvals/${approvalData?.companyId}/${approvalData?.approvalLink}?viewOnly=true`}
                    className="btn btn-sm btn-primary me-2 flex-center"
                    target="_blank"
                  >
                    A
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {loading && <Spinner />}

      {/* Cards */}
      {/* Top Row  */}
      <div className="row  px-1">
        {/* Product Details */}
        <div style={{ padding: "5px 5px" }} className="col-md-6">
          <div
            style={{ maxHeight: 400 }}
            className="px-2 py-2 rounded-3 bg-white shadow-sm h-100"
          >
            <SProductsInfo
              user={selectedWO?.user}
              isUpdateMode={isUpdateMode}
              selectedWO={selectedWO}
              productsList={productsList}
              setProductsList={setProductsList}
              productTypesList={productTypesList}
              fabrics={fabrics}
              formData={formData}
              setFormData={setFormData}
              setUpdateId={setUpdateId}
              setPreviewImage={setPreviewImage}
            />
          </div>
        </div>
        {/* Sizes */}
        <div style={{ padding: "5px 5px" }} className="col-md-6">
          <div style={{ maxHeight: 400, overflow: "hidden" }}>
            <SProductQty
              setFormData={setFormData}
              updateFunc={UpdateFunc}
              formData={formData}
              isProductUpdate={isUpdateMode && !selectMode}
            />
          </div>
        </div>
      </div>
      {/* Bottom Pane */}
      <div className="row px-1">
        {/*   Image select
         ********************************************* */}
        <div style={{ padding: "5px 5px" }} className="col-md-6">
          <div
            className="bg-white shadow-lg p-2 mb-2 d-flex flex-column justify-content-between "
            style={{ borderRadius: 15, minHeight: 350 }}
          >
            {formData?.productImage && !previewImage ? (
              <div className="d-flex justify-content-center align-items-center flex-grow-1">
                <ImageWIthSignedUrl
                  imgStyle={{
                    maxWidth: "100%",
                    maxHeight: 280,
                    objectFit: "contain",
                  }}
                  imgClasses="mb-2"
                  path={formData?.productImage}
                  alt="Image"
                />
              </div>
            ) : (
              <div className="fs-3 text-muted text-center my-auto">
                {!previewImage && "NO Image Selected"}
              </div>
            )}

            {!formData?.productImage && previewImage && (
              <div className="d-flex justify-content-center align-items-center flex-grow-1">
                <img
                  style={{
                    maxWidth: "100%",
                    maxHeight: 280,
                    objectFit: "contain",
                  }}
                  src={previewImage}
                  alt="preview"
                />
              </div>
            )}

            {formData?.productImage ? (
              <button
                onClick={() => {
                  setPreviewImage(null);
                  setFormData((prev) => {
                    return { ...prev, productImage: null };
                  });
                }}
                type="button"
                className="btn btn-sm btn-danger"
              >
                Delete Image
              </button>
            ) : (
              <div className="">
                <input
                  onChange={getBase64Image}
                  type="file"
                  // accept="image/png,"
                  className="form-control"
                />
              </div>
            )}
          </div>
        </div>

        {/*   Notes
         ********************************************* */}
        <div style={{ padding: "5px 5px" }} className="col-md-6">
          <div
            className="bg-white shadow-lg p-2 mb-2 d-flex flex-column justify-content-between "
            style={{ borderRadius: 15, minHeight: 350 }}
          >
            <div className="">
              <ReactQuill
                modules={modules}
                theme="snow"
                style={{
                  height: 260,
                }}
                value={formData?.notes}
                onChange={(val) =>
                  setFormData((prev) => ({ ...prev, notes: val }))
                }
              />
            </div>
          </div>
        </div>
      </div>

      <Modal
        show={productSummaryModal}
        onHide={() => {
          setProductSummaryModal(false);
          setProductTypeSummary([]);
        }}
        contentClassName="custom-shadow-sm custom-border-radius px-2"
        size="lg"
      >
        {/* <Modal.Header closeButton>
          <Modal.Title>
            Product Summary for{" "}
            {selectedProductType ? selectedProductType.productTypes : "All"}
          </Modal.Title>
        </Modal.Header> */}
        <Modal.Body>
          <QuickViewProductSummary
            selected={selectedWO}
            productTypeSummary={productTypeSummary}
            allProducts={allProducts}
            setAllProducts={setAllProducts}
            setProductsList={setProductsList}
            refreshFunc={handleProductSummary}
            productTypesList={productTypesList}
            selectedProductType={selectedProductType}
            setSelectedProductType={setSelectedProductType}
            setProductTypeSummary={setProductTypeSummary}
          />
        </Modal.Body>
      </Modal>
    </fieldset>
  );
}
